import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Container from 'react-bootstrap/Container';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useNavigate } from 'react-router-dom';
import Api_key from './apiconfiguration';
import GlassLoading from './GlassLoading';
import useLoadTransition from './useLoadTransition';
import navigateWithTransition from './navigateWithTransition';

const filters = [
  { id: 'all', label: 'All' },
  { id: 'movie', label: 'Movies' },
  { id: 'tv', label: 'Series' },
];

const Searchresult = ({ search }) => {
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [error, setError] = useState(null);
  const { showLoader, isContentVisible } = useLoadTransition(loading);
  const apiKey = Api_key;
  const navigate = useNavigate();

  const visibleResults = useMemo(
    () => activeFilter === 'all'
      ? searchResults
      : searchResults.filter((item) => item.media_type === activeFilter),
    [activeFilter, searchResults]
  );

  useEffect(() => {
    const controller = new AbortController();
    const fetchSearchResults = async () => {
      if (!search) {
        setSearchResults([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setActiveFilter('all');

      try {
        const responses = await Promise.all(
          Array.from({ length: 3 }, (_, index) =>
            axios.get('https://api.themoviedb.org/3/search/multi', {
              params: { api_key: apiKey, query: search, language: 'en-US', page: index + 1 },
              signal: controller.signal,
            })
          )
        );

        const uniqueResults = new Map();
        responses
          .flatMap((response) => response.data.results || [])
          .filter((item) =>
            (item.media_type === 'movie' || item.media_type === 'tv') &&
            (item.poster_path || item.backdrop_path)
          )
          .forEach((item) => uniqueResults.set(`${item.media_type}-${item.id}`, item));
        setSearchResults([...uniqueResults.values()]);
      } catch (err) {
        if (!axios.isCancel(err)) setError('We could not complete that search. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchSearchResults();
    return () => controller.abort();
  }, [apiKey, search]);

  return (
    <div className='page-stage'>
      {showLoader && (
        <div className={`page-loader-layer search-loader-layer ${loading ? '' : 'is-exiting'}`}>
          <GlassLoading variant='search' label={`Searching for ${search}`} />
        </div>
      )}
    <main className={`search-page-shell page-reveal ${isContentVisible ? 'is-visible' : ''}`}>
      <Container className='search-page'>
        <header className='search-heading'>
          <div className='search-heading-copy'>
            <span className='search-eyebrow'>DISCOVER</span>
            <h1>Results for <em>“{search}”</em></h1>
            <p>{searchResults.length ? `${searchResults.length} movies and series found` : 'Movies and series matching your search'}</p>
          </div>
          {searchResults.length > 0 && (
            <div className='search-filter' aria-label='Filter search results'>
              {filters.map((filter) => (
                <button key={filter.id} className={activeFilter === filter.id ? 'is-active' : ''} onClick={() => setActiveFilter(filter.id)} type='button'>
                  {filter.label}
                  <span>{filter.id === 'all' ? searchResults.length : searchResults.filter((item) => item.media_type === filter.id).length}</span>
                </button>
              ))}
            </div>
          )}
        </header>

        {error && <div className='search-error'>{error}</div>}
        {!error && visibleResults.length === 0 ? (
          <section className='empty-search'>
            <span aria-hidden='true'>⌕</span>
            <h2>No titles found</h2>
            <p>Try a shorter title, a different spelling, or another keyword.</p>
          </section>
        ) : (
          <section className='search-results-grid' aria-live='polite'>
            {visibleResults.map((item) => {
              const title = item.title || item.name;
              const date = item.release_date || item.first_air_date;
              const year = date ? date.slice(0, 4) : 'TBA';
              const rating = item.vote_average ? item.vote_average.toFixed(1) : 'NR';
              return (
                <article key={`${item.media_type}-${item.id}`} className='search-result-card'>
                  <button type='button' className='search-card-action' onClick={() => navigateWithTransition(navigate, `/${item.media_type}/${item.id}`)} aria-label={`View details for ${title}`}>
                    <img src={`https://image.tmdb.org/t/p/w500${item.poster_path || item.backdrop_path}`} alt='' loading='lazy' />
                    <span className='search-card-shade' />
                    <span className='search-card-topline'><b>{item.media_type === 'movie' ? 'MOVIE' : 'SERIES'}</b><i>★ {rating}</i></span>
                    <span className='search-card-copy'><small>{year}</small><strong>{title}</strong><span>View details <b aria-hidden='true'>↗</b></span></span>
                  </button>
                </article>
              );
            })}
          </section>
        )}
      </Container>
    </main>
    </div>
  );
};

export default Searchresult;
